import { useEffect, useRef, useCallback } from 'react';
import { cloneDeep } from 'lodash-es';
import useCos from '../../../../components/QAInput/hooks/useCOS';
import { usePageLifecycle } from '../../../hooks';
import { POSTER_STEP_TYPE } from '../constants';
import { IMagicPosterFileItem } from './useMagicPosterFiles';

/** 自动保存选项 */
export interface IFlushSaveOptions {
  /** 强制指定保存时使用的步骤类型（不传则使用当前步骤） */
  forceStepActive?: string;
}

/** 自动保存上下文：追踪当前文件和编辑状态 */
interface ISaveContext {
  /** 当前文件的 Markdown 内容 */
  currentFileContent: string;
  /** 当前选中的文件 */
  currentFile: IMagicPosterFileItem | undefined;
  /** 当前文件的签名 URL */
  currentFileSignedUrl: string;
  /** 当前步骤页签 */
  stepActive: string;
  /** 是否只读模式 */
  readonly: boolean;
}

/** Hook 入参 */
interface IUsePosterAutoSaveParams {
  /** 当前文件的 Markdown 内容 */
  currentFileContent: string;
  /** 当前选中的文件 */
  currentFile: IMagicPosterFileItem | undefined;
  /** 当前文件的签名 URL */
  currentFileSignedUrl: string;
  /** 当前步骤页签 */
  stepActive: string;
  /** 是否只读模式 */
  readonly: boolean;
  /** Markdown 编辑器引用 */
  markdownRef: React.MutableRefObject<any>;
}

/** Hook 返回类型 */
export interface IUsePosterAutoSaveReturn {
  /** 执行保存：获取编辑器 Markdown → 上传 COS */
  doSave: (options?: IFlushSaveOptions) => Promise<void>;
  /** 立即保存并重建快照 */
  flushSave: (options?: IFlushSaveOptions) => Promise<void>;
}

/** 标识无变更的占位符 */
const AUTO_SAVE_NO_CHANGE = 'no_change';

/**
 * 海报自动保存 Hook（仅内容规划页签）
 *
 * 功能：
 * 1. 维护 Markdown 内容快照，用于脏检测
 * 2. 文件切换时自动重置快照
 * 3. 内容规划页签下首次加载时初始化快照
 * 4. 提供 doSave（执行保存）和 flushSave（立即保存并重建快照）
 * 5. 通过 usePageLifecycle 监听页面离开事件，自动触发保存
 */
export const usePosterAutoSave = ({
  currentFileContent,
  currentFile,
  currentFileSignedUrl,
  stepActive,
  readonly,
  markdownRef,
}: IUsePosterAutoSaveParams): IUsePosterAutoSaveReturn => {
  const { putObject } = useCos();

  /** 内容快照：用于判断是否有变更 */
  const snapshotRef = useRef<string>('');
  /** 快照是否已初始化 */
  const snapshotInitializedRef = useRef(false);
  /** 是否正在保存中（防止并发） */
  const isSavingRef = useRef(false);

  /** 保存上下文引用：在回调中始终读取最新值 */
  const saveContextRef = useRef<ISaveContext>({
    currentFileContent,
    currentFile,
    currentFileSignedUrl,
    stepActive,
    readonly,
  });

  /** 同步保存上下文 */
  useEffect(() => {
    saveContextRef.current = {
      currentFileContent,
      currentFile,
      currentFileSignedUrl,
      stepActive,
      readonly,
    };
  }, [currentFileContent, currentFile, currentFileSignedUrl, stepActive, readonly]);

  /** 文件切换时重置快照 */
  const prevFileIdRef = useRef(currentFile?.id);
  useEffect(() => {
    if (prevFileIdRef.current !== currentFile?.id) {
      prevFileIdRef.current = currentFile?.id;
      snapshotRef.current = '';
      snapshotInitializedRef.current = false;
    }
  }, [currentFile?.id]);

  /** 内容规划页签下首次加载时初始化快照 */
  useEffect(() => {
    if (snapshotInitializedRef.current) return;
    if (stepActive !== POSTER_STEP_TYPE.FRAMEWORK) return;
    if (!currentFileSignedUrl || !currentFileContent) return;
    snapshotRef.current = cloneDeep(currentFileContent);
    snapshotInitializedRef.current = true;
    markdownRef.current?.resetDirty?.();
  }, [currentFileContent, currentFileSignedUrl, stepActive]);

  /** 执行保存：获取编辑器 Markdown → 上传 COS */
  const doSave = useCallback(async (options?: IFlushSaveOptions) => {
    const ctx = saveContextRef.current;
    const effectiveStepActive = options?.forceStepActive ?? ctx.stepActive;

    if (ctx.readonly || isSavingRef.current) return;
    if (effectiveStepActive !== POSTER_STEP_TYPE.FRAMEWORK) return;
    if (!ctx.currentFileSignedUrl) return;

    const dirtyCheck = markdownRef.current?.isDirty;
    if (typeof dirtyCheck === 'function' && !dirtyCheck()) return;

    let currentMarkdown: string;
    try {
      currentMarkdown = await markdownRef.current?.getMarkdown?.() || '';
    } catch {
      return;
    }
    if (!currentMarkdown) return;

    if (snapshotInitializedRef.current && currentMarkdown === snapshotRef.current) return;

    isSavingRef.current = true;
    try {
      const cosUrl = ctx.currentFileSignedUrl;
      const filePath = cosUrl.replace(/^https?:\/\/[^/]+/, '').replace(/\?.*$/, '');
      if (!filePath) return;

      const blob = new Blob([currentMarkdown], { type: 'text/markdown; charset=utf-8' });
      await putObject({ path: filePath, file: blob });

      snapshotRef.current = cloneDeep(currentMarkdown);
      snapshotInitializedRef.current = true;
      markdownRef.current?.resetDirty?.();
    } catch (error) {
      console.error('[PosterAutoSave] 自动保存失败:', error);
    } finally {
      isSavingRef.current = false;
    }
  }, [putObject, markdownRef]);

  /** 立即保存并重建快照 */
  const flushSave = useCallback(async (options?: IFlushSaveOptions) => {
    const currentStep = saveContextRef.current.stepActive;
    await doSave({
      forceStepActive: options?.forceStepActive ?? currentStep,
    });

    snapshotRef.current = '';
    snapshotInitializedRef.current = false;
  }, [doSave]);

  /** 用于 usePageLifecycle 的脏检测代理引用 */
  const diffRenderRef = useRef({
    getMarkdown: () => {
      const ctx = saveContextRef.current;
      if (ctx.stepActive !== POSTER_STEP_TYPE.FRAMEWORK) return AUTO_SAVE_NO_CHANGE;
      if (!ctx.currentFileSignedUrl) return AUTO_SAVE_NO_CHANGE;
      const dirtyCheck = markdownRef.current?.isDirty;
      if (typeof dirtyCheck === 'function') {
        return dirtyCheck() ? String(Date.now()) : AUTO_SAVE_NO_CHANGE;
      }
      return AUTO_SAVE_NO_CHANGE;
    },
  });

  /** 页面离开时自动触发保存 */
  usePageLifecycle({
    value: AUTO_SAVE_NO_CHANGE,
    isDiffRender: diffRenderRef.current,
    callback: doSave,
  });

  return {
    doSave,
    flushSave,
  };
};

export default usePosterAutoSave;
